// 로드 때만 골격을 평가하고, 렌더 중에는 인스턴스별 프레임 번호 세 개만 갱신한다.
import * as THREE from 'three/webgpu';
import * as TSL from 'three/tsl';
import { PoseVertexReader } from '../poseVertexReader';

export interface BakedClip {
  start: number;
  count: number;
  duration: number;
  /** 모델 공간에서 디딘 발의 엉덩이 기준 속도(m/s). */
  speed: number;
  /** 사족 절차 클립이 접지 중 발을 앞으로 밀면 역재생해 뒤로 디디게 한다. */
  direction?: number;
  stanceZ?: number;
  speedMethod?: 'bone' | 'sole';
}
export interface SkinAtlas {
  texture: THREE.DataTexture;
  clips: Record<string, BakedClip>;
  bones: number;
}

/** 월드 공간으로 펼친 지오메트리에 대응하는 최종 행렬을 3×4 행으로 저장한다. */
export function packSkinMatrix(matrix: THREE.Matrix4, out: Float32Array, offset: number): void {
  const e = matrix.elements;
  for (let r = 0; r < 3; r++) for (let c = 0; c < 4; c++) out[offset + r * 4 + c] = e[c * 4 + r];
}

/** 끝 프레임은 반복하지 않는 클립에서 반드시 보존한다. */
export function clipFrame(atlas: SkinAtlas, name: string, seconds: number, loop = false): number {
  const c = atlas.clips[name] ?? atlas.clips.idle;
  if (!c) return 0;
  const t = loop ? ((seconds % c.duration) + c.duration) % c.duration : Math.max(0, Math.min(seconds, c.duration));
  return c.start + t / c.duration * (c.count - 1);
}

export function bakeSkin(root: THREE.Object3D, mesh: THREE.SkinnedMesh, clips: THREE.AnimationClip[], floorClamp = false): SkinAtlas {
  const bones = mesh.skeleton.bones.length;
  const table: Record<string, BakedClip> = {};
  let frames = 0;
  for (const c of clips) {
    if (c.duration <= 0 || table[c.name]) throw new Error('클립 길이 또는 이름 중복: ' + c.name);
    const count = Math.max(1, Math.round(c.duration * 30)) + 1;
    table[c.name] = { start: frames, count, duration: c.duration, speed: 0 };
    frames += count;
  }
  if (!bones || !frames || frames > 8192 || bones * 3 > 8192) throw new Error('골격 텍스처 크기 초과');
  const data = new Float32Array(frames * bones * 12);
  root.updateMatrixWorld(true);
  // GLB 양자화의 메시 변환도 포함한다. 고해상도/LOD의 양자화 변환은 다를 수 있다.
  const world = mesh.matrixWorld.clone(), inverseWorld = world.clone().invert();
  const bind = mesh.bindMatrix.clone(), inverseBind = mesh.bindMatrixInverse.clone();
  const left = world.clone().multiply(inverseBind), right = bind.clone().multiply(inverseWorld);
  const matrix = new THREE.Matrix4(), bone = new THREE.Matrix4();
  const mixer = new THREE.AnimationMixer(root);
  const feet = mesh.skeleton.bones.filter(b => /Foot$/.test(b.name));
  const hips = mesh.skeleton.bones.find(b => /Hips$/.test(b.name));
  const v = new THREE.Vector3(), h = new THREE.Vector3();
  // 발목 본은 발바닥과 회전 반경이 다르다. 실제 스킨의 하단 정점으로 디딤 속도를 보정한다.
  mesh.computeBoundingBox();
  const bounds = mesh.boundingBox!;
  const footVertices: number[] = [];
  const sourcePosition = mesh.geometry.getAttribute('position');
  const vertices = new PoseVertexReader(mesh, false);
  vertices.update();
  for (let i = 0; i < sourcePosition.count; i++) {
    vertices.local(i, v);
    if (v.y < bounds.min.y + (bounds.max.y - bounds.min.y) * 0.16) footVertices.push(i);
  }
  for (const clip of clips) {
    const c = table[clip.name];
    const action = mixer.clipAction(clip);
    action.setLoop(THREE.LoopOnce, 1); action.clampWhenFinished = true; action.play();
    const footSamples = feet.map(() => new Float32Array(c.count * 3));
    const useSole = clip.name === 'run' || clip.name === 'charge';
    const sole = new Float32Array(footVertices.length * 3), previousSole = new Float32Array(sole.length);
    const soleSpeeds: number[] = [], soleAlong: number[] = [];
    let previousFloor = Infinity;
    let floor = Infinity;
    for (let f = 0; f < c.count; f++) {
      action.time = f * c.duration / (c.count - 1);
      mixer.update(0); root.updateMatrixWorld(true); mesh.skeleton.update();
      if (floorClamp || useSole) vertices.update();
      // 갓까지 포함한 전체 메시 최저점을 굽는 시점에 보정한다. 프레임 사이 선형 보간도 바닥 아래로 내려가지 않는다.
      let lift = 0;
      if (floorClamp) {
        let minY = Infinity;
        for (let i = 0; i < sourcePosition.count; i++) {
          vertices.world(i, v, world);
          minY = Math.min(minY, v.y);
        }
        lift = Math.max(0, -minY);
      }
      for (let b = 0; b < bones; b++) {
        bone.fromArray(mesh.skeleton.boneMatrices!, b * 16);
        matrix.copy(left).multiply(bone).multiply(right);
        matrix.elements[13] += lift;
        packSkinMatrix(matrix, data, ((c.start + f) * bones + b) * 12);
      }
      if (hips) hips.getWorldPosition(h); else h.set(0, 0, 0);
      for (let j = 0; j < feet.length; j++) {
        feet[j].getWorldPosition(v);
        const a = footSamples[j], o = f * 3;
        a[o] = v.x - h.x; a[o + 1] = v.y; a[o + 2] = v.z - h.z;
        floor = Math.min(floor, v.y);
      }
      if (useSole) {
        let currentFloor = Infinity;
        for (let j = 0; j < footVertices.length; j++) {
          vertices.world(footVertices[j], v, world).toArray(sole, j * 3);
          currentFloor = Math.min(currentFloor, v.y);
        }
        const dt = c.duration / (c.count - 1);
        if (f > 0 && currentFloor < 0.04 && previousFloor < 0.04) for (let j = 0; j < footVertices.length; j++) {
          const o = j * 3;
          if (sole[o + 1] > currentFloor + 0.025 || previousSole[o + 1] > previousFloor + 0.025) continue;
          const dx = (sole[o] - previousSole[o]) / dt, dz = (sole[o + 2] - previousSole[o + 2]) / dt;
          if (Math.abs(dz) > 0.03) { soleSpeeds.push(Math.hypot(dx, dz)); soleAlong.push(dz); }
        }
        previousSole.set(sole); previousFloor = currentFloor;
      }
    }
    const speeds: number[] = [], along: number[] = [];
    const dt = c.duration / (c.count - 1);
    for (const a of footSamples) for (let f = 1; f < c.count; f++) {
      const o = f * 3;
      if (a[o + 1] > floor + 0.04 || a[o - 2] > floor + 0.04) continue;
      const speed = Math.hypot(a[o] - a[o - 3], a[o + 2] - a[o - 1]) / dt;
      if (speed > 0.03) { speeds.push(speed); along.push((a[o + 2] - a[o - 1]) / dt); }
    }
    speeds.sort((a, b) => a - b);
    c.speed = speeds.length ? speeds[speeds.length >> 1] : 0;
    along.sort((a, b) => a - b);
    c.stanceZ = along.length ? along[along.length >> 1] : 0;
    c.direction = c.stanceZ > 0.03 ? -1 : 1;
    c.speedMethod = 'bone';
    if (soleAlong.length >= 8) {
      const sorted = soleAlong.slice().sort((a, b) => a - b);
      const z = sorted[sorted.length >> 1], direction = z > 0 ? -1 : 1;
      const planted = soleSpeeds.filter((_, i) => soleAlong[i] * direction < -0.03).sort((a, b) => a - b);
      if (planted.length >= 8) {
        c.speed = planted[planted.length >> 1]; c.direction = direction; c.stanceZ = z; c.speedMethod = 'sole';
      }
    }
    action.stop();
  }
  mixer.stopAllAction(); mixer.uncacheRoot(root);
  const texture = new THREE.DataTexture(data, bones * 3, frames, THREE.RGBAFormat, THREE.FloatType);
  texture.minFilter = texture.magFilter = THREE.NearestFilter;
  texture.generateMipmaps = false; texture.needsUpdate = true;
  texture.name = mesh.name + '-skin30';
  return { texture, clips: table, bones };
}

// TSL 그래프만 느슨하게 타이핑한다(런타임 데이터·상태 매핑은 엄격 타입).
type N = any; // eslint-disable-line @typescript-eslint/no-explicit-any
const { Fn, attribute, floor, fract, int, ivec2, mat4, mix, normalize, textureLoad, vec4 } = TSL as unknown as Record<string, N>;

/** 포즈=(클립A 프레임, 클립B 프레임, B 비율). 두 클립 모두 30fps 사이를 보간한다. */
export function skinNodes(atlas: SkinAtlas, pose: N, position: N, normal: N, smooth: N): { position: N; normal: N; smooth: N } {
  const indices = attribute('skinIndex', 'vec4'), weights = attribute('skinWeight', 'vec4');
  const matrix = Fn(() => {
    const row = (r: number): N => {
      let sum: N = vec4(0);
      for (const component of ['x', 'y', 'z', 'w']) {
        const x = int(indices[component]).mul(3).add(r);
        const at = (frame: N): N => {
          const y = int(floor(frame));
          // 텍스처 마지막 행을 넘지 않는다(클립 끝은 보간 비율이 0).
          const next = y.add(1).min(atlas.texture.image.height - 1);
          return mix(textureLoad(atlas.texture, ivec2(x, y)), textureLoad(atlas.texture, ivec2(x, next)), fract(frame));
        };
        sum = sum.add(mix(at(pose.x), at(pose.y), pose.z).mul(weights[component]));
      }
      return sum.toVar();
    };
    const a = row(0), b = row(1), c = row(2);
    return mat4(vec4(a.x, b.x, c.x, 0), vec4(a.y, b.y, c.y, 0), vec4(a.z, b.z, c.z, 0), vec4(a.w, b.w, c.w, 1));
  })().toVar();
  return { position: matrix.mul(vec4(position, 1)).xyz, normal: normalize(matrix.mul(vec4(normal, 0)).xyz), smooth: normalize(matrix.mul(vec4(smooth, 0)).xyz) };
}
